/**
 * Board labels — a per-board palette, and the id references tasks hold.
 *
 * Pure, for the same reason boardFilters.js is: TaskBoardTab cannot be
 * exercised without a DOM, and the two rules that matter here are both
 * invisible when they break.
 *
 * THE PALETTE LIVES ON THE BOARD; TASKS HOLD IDS.
 *
 *   board.labels = [{ id, name, color }, ...]
 *   task.labels  = [labelId, ...]
 *
 * Not embedded copies. A label is a shared vocabulary — the whole point is
 * that "bug" means the same thing on every card — so renaming or recolouring
 * one has to change every card at once. Embedding the object on each task
 * would fork the vocabulary silently: rename it, and old cards keep the old
 * name forever with no indication anything is stale.
 *
 * The cost of ids is dangling references when a label is deleted from the
 * palette. That is paid at RENDER (resolveLabels drops unknown ids) rather
 * than by rewriting every task on delete — a palette edit must not turn into
 * a hundred task writes that sync as a hundred records, and a task rewritten
 * behind your back is a task whose `updatedAt` lies.
 */

/**
 * The colours a label can take. Fixed, not free-form: a colour picker on a
 * label produces boards where six barely-distinguishable greens each mean
 * something, and the palette exists to be scanned at a glance.
 *
 * Values are literals rather than CSS variables because they are stored on the
 * board and travel through sync — a variable name would resolve differently in
 * light and dark mode on different devices, for the same label.
 */
export const LABEL_COLORS = [
  // `dot` is the saturated swatch (the pill's dot, filter dots, tile dots);
  // `tint` the light text a pill reads in on a dark surface. Both feed
  // pillColors below — the one place a pill's colours are derived.
  { key: 'gray', name: 'Grey', bg: '#e4e4e7', fg: '#3f3f46', darkBg: '#3f3f46', darkFg: '#e4e4e7', dot: '#a1a1aa', tint: '#e4e4e7' },
  { key: 'red', name: 'Red', bg: '#fee2e2', fg: '#991b1b', darkBg: '#7f1d1d', darkFg: '#fecaca', dot: '#f87171', tint: '#fecaca' },
  // Orange and pink joined with the Edit board redesign (card 5.2, 12.10.2026):
  // the design of record offers eight. An older build reads them as gray
  // (labelColor's fallback), never as an unstyled chip.
  { key: 'orange', name: 'Orange', bg: '#ffedd5', fg: '#9a3412', darkBg: '#7c2d12', darkFg: '#fed7aa', dot: '#fb923c', tint: '#fed7aa' },
  { key: 'amber', name: 'Yellow', bg: '#fef3c7', fg: '#92400e', darkBg: '#78350f', darkFg: '#fde68a', dot: '#fbbf24', tint: '#fde68a' },
  { key: 'green', name: 'Green', bg: '#dcfce7', fg: '#166534', darkBg: '#14532d', darkFg: '#bbf7d0', dot: '#34d399', tint: '#a7f3d0' },
  { key: 'blue', name: 'Blue', bg: '#dbeafe', fg: '#1e40af', darkBg: '#1e3a8a', darkFg: '#bfdbfe', dot: '#60a5fa', tint: '#bfdbfe' },
  { key: 'purple', name: 'Purple', bg: '#ede9fe', fg: '#5b21b6', darkBg: '#4c1d95', darkFg: '#ddd6fe', dot: '#a78bfa', tint: '#ddd6fe' },
  { key: 'pink', name: 'Pink', bg: '#fce7f3', fg: '#9d174d', darkBg: '#831843', darkFg: '#fbcfe8', dot: '#f472b6', tint: '#fbcfe8' },
];

export const DEFAULT_LABEL_COLOR = 'gray';

/** A colour entry by key, falling back to gray for a key written by a newer
 *  build (or corrupted through sync) rather than rendering an unstyled chip. */
export function labelColor(key) {
  return LABEL_COLORS.find((c) => c.key === key) || LABEL_COLORS[0];
}

/** `#rrggbb` at `alpha`, as rgba(). */
function withAlpha(hex, alpha) {
  const n = parseInt(String(hex).slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * The colours of a label PILL (Boards Redesign card 2.3): a 6 px dot in the
 * label's colour, the colour at 12 % as the background, and a light tint of it
 * as the text. One helper for every place a label is drawn as a pill — card,
 * list row, filter chips, the Edit board preview, the overview's dots — so a
 * colour cannot read one way on the card and another in the filter bar.
 *
 * Takes a resolved label (`{ color }`), a palette entry or a colour key.
 * Returns `{ bg, fg, dot }` for the dark theme (the design of record) by
 * default; `{ theme: 'light' }` gives the light equivalent (the dot at 16 % on
 * a white surface, the deep shade as text). A component that renders in both
 * themes uses pillStyleVars instead and lets CSS pick.
 */
export function pillColors(label, { theme = 'dark' } = {}) {
  const key = typeof label === 'string' ? label : label?.color ?? label?.key;
  const c = labelColor(key);
  if (theme === 'light') return { bg: withAlpha(c.dot, 0.16), fg: c.fg, dot: c.dot };
  return { bg: withAlpha(c.dot, 0.12), fg: c.tint, dot: c.dot };
}

/**
 * The same colours as CSS custom properties for an inline `style`, both themes
 * at once: `--pill-bg` / `--pill-fg` (light), `--pill-bg-dark` /
 * `--pill-fg-dark`, `--pill-dot`. `.task-label-chip` (TaskLabelChips.css)
 * reads them; any other pill can reuse that class or the variables.
 */
export function pillStyleVars(label) {
  const light = pillColors(label, { theme: 'light' });
  const dark = pillColors(label, { theme: 'dark' });
  return {
    '--pill-bg': light.bg,
    '--pill-fg': light.fg,
    '--pill-bg-dark': dark.bg,
    '--pill-fg-dark': dark.fg,
    '--pill-dot': dark.dot,
  };
}

/** The board's palette, normalised. Anything malformed is dropped rather than
 *  rendered as a nameless chip. */
export function paletteOf(board) {
  const list = Array.isArray(board?.labels) ? board.labels : [];
  return list.filter((l) => l && l.id != null).map((l) => ({
    id: String(l.id),
    name: typeof l.name === 'string' ? l.name : '',
    color: l.color || DEFAULT_LABEL_COLOR,
  }));
}

/** The ids a task holds, normalised and de-duplicated. */
export function labelIdsOf(task) {
  const list = Array.isArray(task?.labels) ? task.labels : [];
  return [...new Set(list.filter((id) => id != null).map(String))];
}

/**
 * A task's labels resolved against the board's palette, in PALETTE order.
 *
 * Palette order, not the order the task happens to store them in: labels are
 * scanned across a column, and chips that appear in a different order on every
 * card cannot be scanned at all.
 *
 * Ids with no palette entry are dropped — that is a label deleted from the
 * board, and a chip reading "unknown" on every affected card would be worse
 * than the label quietly going away.
 */
export function resolveLabels(task, board) {
  const held = new Set(labelIdsOf(task));
  return paletteOf(board).filter((l) => held.has(l.id));
}

/**
 * Does the task carry ANY of the selected labels?
 *
 * ANY, not ALL. "Show me bugs and regressions" is the question people ask of a
 * label filter; ALL would mean "cards that are both", which is almost always
 * an empty board and reads as broken. The other filters still intersect with
 * this one — Mine + (bug OR regression) is my bugs and my regressions.
 */
export function matchesLabels(task, selectedIds) {
  const wanted = (selectedIds || []).map(String);
  if (wanted.length === 0) return true;
  const held = new Set(labelIdsOf(task));
  return wanted.some((id) => held.has(id));
}

/** Toggle one label on a task, returning the new id array. Never mutates. */
export function toggleTaskLabel(task, labelId) {
  const id = String(labelId);
  const held = labelIdsOf(task);
  return held.includes(id) ? held.filter((x) => x !== id) : [...held, id];
}

/**
 * Add a label to a board's palette. Names are trimmed and compared
 * case-insensitively: two labels called "Bug" and "bug" are the same
 * vocabulary word wearing different capitalisation, and allowing both is how a
 * shared palette stops being shared.
 */
export function addLabel(board, { id, name, color = DEFAULT_LABEL_COLOR }) {
  const trimmed = (name || '').trim();
  if (!trimmed || !id) return paletteOf(board);
  const palette = paletteOf(board);
  if (palette.some((l) => l.name.toLowerCase() === trimmed.toLowerCase())) return palette;
  return [...palette, { id: String(id), name: trimmed, color }];
}

/** Rename/recolour one palette entry. Every card showing it updates, which is
 *  the entire reason tasks hold ids. */
export function updateLabel(board, labelId, patch) {
  const id = String(labelId);
  return paletteOf(board).map((l) =>
    l.id === id
      ? {
          ...l,
          ...(patch.name != null ? { name: String(patch.name).trim() || l.name } : {}),
          ...(patch.color != null ? { color: patch.color } : {}),
        }
      : l
  );
}

/** Remove one palette entry. Tasks keep the dangling id; resolveLabels drops
 *  it at render (see the module note). */
export function removeLabel(board, labelId) {
  const id = String(labelId);
  return paletteOf(board).filter((l) => l.id !== id);
}
